/**
 * OwnOrganisationsSection (#3371) — Settings → Organisationen ("Eigene Organisationen").
 *
 * The organisations that are part of the farm group itself, an add-picker restricted to
 * organisations already in the twin, and a remove that clears the flag (it never deletes the
 * organisation).
 *
 * Since the settings refactor this is a **pane** (`/settings/organisations`), mounted only while
 * open — so its twin query (``OwnOrganisationsManager``) runs only for someone looking at it, the
 * job the old "Verwalten" toggle did.
 *
 * The optimistic overlay is owned by ``SettingsPage``, one level up, because this pane unmounts
 * whenever another pane opens. Held here, leaving and coming back would drop it and re-serve the
 * 60 s-cached PRE-edit list — an organisation the farmer just removed would reappear, reading
 * exactly like a save that failed.
 */
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { SettingsPane } from '@hofsync/design-system';
import OwnOrganisationsManager from './OwnOrganisationsManager';
import type { OwnOrgOverlay } from './ownOrganisations';
import type { SettingsPaneChrome } from './paneChrome';

export interface OwnOrganisationsSectionProps extends SettingsPaneChrome {
    overlay: OwnOrgOverlay;
    setOverlay: React.Dispatch<React.SetStateAction<OwnOrgOverlay>>;
}

export default function OwnOrganisationsSection({ overlay, setOverlay, backLink, focusOnMount }: OwnOrganisationsSectionProps) {
    const { t } = useTranslation();
    return (
        <SettingsPane
            title={t('settings.ownOrganisations.title', 'Eigene Organisationen')}
            description={t(
                'settings.ownOrganisations.subtitle',
                'Betriebe und Verkaufsstellen, die zu deinem eigenen Betrieb gehören. Zahlungen an sie werden nicht als Erlös aus dem Verkauf an Dritte gebucht.'
            )}
            backLink={backLink}
            focusOnMount={focusOnMount}
        >
            <OwnOrganisationsManager overlay={overlay} setOverlay={setOverlay} />
        </SettingsPane>
    );
}
